import React from "react";
import WComponent from "../../../../../common/WComponent";
import { formatReportAmount, formatCurrency, formatDate, chooseSignedCaption, titleForRetainedEarningsStatement } from "../../shared/helpers/formatters";
import { NOTE_TEMPLATES } from "../../shared/helpers/constants";
import BookkeepingService from "../../../../../../services/BookkeepingService";
import ToolsInlineEdit from "../../../kit/ToolsInlineEdit";
import ToolsCheckbox from "../../../kit/ToolsCheckbox";
import "../../afs-walkthrough.css";

const fmtAmt = formatReportAmount;
const fmtCurrency = formatCurrency;
const fmtDate = formatDate;

class FullPreviewView extends WComponent {
	state = { showDraftWatermark: true };

	render() {
	let { compilationData, entityName, clientProfile, yearEnd, ppeNote, retainedEarnings, currentYear, priorYear, showComparatives, comparativeProvenance, compilationReportSignature, compilationReport, periodDescription, roundedPresentation, statementRows, approvalBlock, noteTexts, noteIds: noteIdsProp, customNotes, onChangeStatementSetting, statementEntityName, isFinalized } = this.props;
	const balanceSheetRows = statementRows && statementRows.balanceSheet;
	const incomeStatementRows = statementRows && statementRows.incomeStatement;
	const approval = approvalBlock || { heading: "APPROVED BY THE DIRECTOR", signatureLines: ["_____________________________ Director"] };
	const statementsAreADraft = !isFinalized;
	const watermarkIsShowing = statementsAreADraft && this.state.showDraftWatermark;
	const retainedEarningsTitle = titleForRetainedEarningsStatement({ retainedEarnings, showComparatives });
	const periodHeading = periodDescription ? periodDescription.statementHeading : "";
	const roundingAdjustments = (roundedPresentation && roundedPresentation.roundingAdjustments) || [];
	const hasPriorPeriod = comparativeProvenance ? comparativeProvenance.kind !== "none" : false;
	const openingBalanceLineIsMissing = !!retainedEarnings && retainedEarnings.openingBalanceWasOnTheBalanceSheet === false;
	const openingBalanceIsUnexplainedNil = openingBalanceLineIsMissing && hasPriorPeriod;

	const customNoteTitleById = BookkeepingService.buildCustomNoteTitleMap({ customNotes });
	const isCustomNote = id => Object.prototype.hasOwnProperty.call(customNoteTitleById, id);
	const hasNoteBody = id => !!(noteTexts && noteTexts[id]);
	const yearEndLabel = yearEnd ? fmtDate(yearEnd) : "[Year End]";
	const signature    = compilationReportSignature || {};
	const reportBody   = (compilationReport && compilationReport.paragraphs) || [];
	const reportDate   = signature.reportDate ? fmtDate(signature.reportDate) : "";
	const entity       = statementEntityName || (clientProfile && clientProfile.entityName) || entityName || "[Entity Name]";
	const firm         = signature.firmName || "";
	const firmCity     = signature.city || "";

	const cols = showComparatives ? 2 : 1;
	const selectedNotes = noteIdsProp || (compilationData.notes || []).filter(id => NOTE_TEMPLATES[id]);

	// Header row used across all financial statement pages
	const PageHeader = ({ title, subtitle }) => (
		<>
			<p className="afs-report__entity-name">{entity}</p>
			<p className={`afs-report__page-title ${subtitle ? "afs-report__page-title--flush" : "afs-report__page-title--spaced"}`}>{title}</p>
			{subtitle && <p className="afs-report__page-subtitle">{subtitle}</p>}
		</>
	);

	const YearHeaders = () => (
		<tr>
			<th />
			<th className="afs-report__th-right">{currentYear}</th>
			{showComparatives && <th className="afs-report__th-right">{priorYear}</th>}
		</tr>
	);

	// ─── Page 1: Title Page ──────────────────────────────────────────────────
	const TitlePage = () => (
		<div className="afs-report__page afs-report__page--cover">
			<p className="afs-report__entity-name afs-report__entity-name--cover">{entity}</p>
			<p className="afs-report__cover-line afs-report__cover-line--gap">Financial Information</p>
			<p className="afs-report__cover-line">{periodHeading} {yearEndLabel}</p>
		</div>
	);

	// ─── Page 2: Index ───────────────────────────────────────────────────────
	const IndexPage = () => {
		const items = [
			{ label: "Compilation Engagement Report", page: 1 },
			{ label: "Balance Sheet", page: 2 },
			{ label: retainedEarningsTitle, page: 3 },
			{ label: "Statement of Income", page: 4 },
		];
		if (selectedNotes.length > 0) items.push({ label: "Notes to Financial Information", page: 5 });

		return (
			<div className="afs-report__page">
				<PageHeader title="Index to Financial Information" subtitle={`${periodHeading} ${yearEndLabel}`} />
				<table className="afs-report__table afs-report__table--mt-xl">
					<thead>
						<tr><th className="afs-report__index-th" /><th className="afs-report__index-th--right">Page</th></tr>
					</thead>
					<tbody>
						{items.map((item, i) => (
							<tr key={i}>
								<td className={`afs-report__index-td${item.label === "Compilation Engagement Report" ? " afs-report__index-td--strong" : ""}`}>{item.label}</td>
								<td className="afs-report__index-td--page">{item.page}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		);
	};

	// ─── Page 3: Compilation Engagement Report ───────────────────────────────
	const CompilationPage = () => (
		<div className="afs-report__page">
			<h2 className="afs-report__h2 afs-report__h2--report">Compilation Engagement Report</h2>
			<p className="afs-report__text afs-report__text--addressee">
				To the Management of{" "}
				<ToolsInlineEdit
					value={entity}
					placeholder="Add the name the statements are headed with"
					label="the name on the statements"
					disabled={!onChangeStatementSetting}
					onChange={({ value }) => onChangeStatementSetting({ setting: "statementEntityName", value })}
				/>
			</p>
			{reportBody.map((paragraph, index) => (
				<p key={index} className="afs-report__text">{paragraph}</p>
			))}
			<div className="afs-report__signature">
				<p className="afs-report__text-sm afs-report__text-sm--tight">
					<ToolsInlineEdit
						value={firmCity}
						placeholder="Add the city the report is signed in"
						label="the city this report is signed in"
						disabled={!onChangeStatementSetting}
						onChange={({ value }) => onChangeStatementSetting({ setting: "compilationReportCity", value })}
					/>
				</p>
				{firm && <p className="afs-report__text-sm afs-report__text-sm--firm">{firm}</p>}
				<p className="afs-report__text-sm afs-report__text-sm--tight">
					<ToolsInlineEdit
						value={signature.reportDate || ""}
						displayValue={reportDate}
						type="date"
						placeholder="Add a report date"
						label="the report date"
						disabled={!onChangeStatementSetting}
						onChange={({ value }) => onChangeStatementSetting({ setting: "compilationReportDate", value })}
					/>
				</p>
				<p className="afs-report__text-sm afs-report__text-sm--designation">Chartered Professional Accountants</p>
			</div>
			<p className="afs-report__page-num">1</p>
		</div>
	);

	const StatementRows = ({ rows }) => rows.map((row, position) => {
		if (row.kind === 'spacer') return <tr key={position}><td className="afs-report__spacer-md" colSpan={1 + cols} /></tr>;

		if (row.kind === 'heading') {
			const headingClass = row.level === 0 ? 'afs-report__section-head' : 'afs-report__subhead';
			return <tr key={position}><td className={headingClass} colSpan={1 + cols}>{row.caption}</td></tr>;
		}

		const captionClass = {
			line: row.level === 2 ? 'afs-report__td-desc-indent' : 'afs-report__td-desc',
			sectionLine: 'afs-report__section-head',
			subtotal: 'afs-report__td-desc afs-report__td-desc--bold',
			result: 'afs-report__td-desc afs-report__td-desc--bold',
			total: 'afs-report__td-desc afs-report__td-desc--bold',
		}[row.kind];

		const amountClass = row.kind === 'line' ? 'afs-report__td-amt' : 'afs-report__td-amt-bold';
		const rowClass = { subtotal: 'afs-report__total-row', result: 'afs-report__total-row', total: 'afs-report__grand-total-row' }[row.kind];
		const format = row.kind === 'total' || row.showsCurrencySymbol ? fmtCurrency : fmtAmt;

		return (
			<tr key={position} className={rowClass}>
				<td className={captionClass}>{row.caption}</td>
				<td className={amountClass}>{format(row.current)}</td>
				{showComparatives && <td className={amountClass}>{format(row.prior)}</td>}
			</tr>
		);
	});

	// ─── Page 4: Balance Sheet ───────────────────────────────────────────────
	const BalanceSheetPage = () => {
		if (!balanceSheetRows) return null;

		return (
			<div className="afs-report__page">
				<PageHeader title="Balance Sheet" subtitle={yearEndLabel} />
				<table className="afs-report__table afs-report__table--mt-md">
					<thead><YearHeaders /></thead>
					<tbody><StatementRows rows={balanceSheetRows} /></tbody>
				</table>
				<p className="afs-report__text-sm afs-report__approval">{approval.heading}</p>
				{approval.signatureLines.map((line, position) => (
					<p key={position} className="afs-report__text-sm afs-report__signature-line">{line}</p>
				))}
				<p className="afs-report__page-num">2</p>
			</div>
		);
	};

	// ─── Page 5: Statement of Retained Earnings ──────────────────────────────
	const RetainedEarningsPage = () => {
		if (!retainedEarnings) return null;
		const printed = pair => (showComparatives ? [pair.current, pair.prior] : [pair.current]);
		const openingCaption = chooseSignedCaption({ amounts: printed(retainedEarnings.openingBalance), positive: "Retained Earnings — Beginning of Year", negative: "Deficit — Beginning of Year", mixed: "Retained Earnings (Deficit) — Beginning of Year" });
		const earningsCaption = chooseSignedCaption({ amounts: printed(retainedEarnings.netIncome), positive: "Net Income", negative: "Net Loss", mixed: "Net Income (Loss)" });
		const closingCaption = chooseSignedCaption({ amounts: printed(retainedEarnings.closingBalance), positive: "Retained Earnings — End of Year", negative: "Deficit — End of Year", mixed: "Retained Earnings (Deficit) — End of Year" });
		return (
			<div className="afs-report__page">
				<PageHeader title={retainedEarningsTitle} subtitle={`${periodHeading} ${yearEndLabel}`} />
				<table className="afs-report__table afs-report__table--mt-lg">
					<thead><YearHeaders /></thead>
					<tbody>
						<tr>
							<td className="afs-report__td-desc afs-report__td-desc--head">{openingCaption}</td>
							<td className="afs-report__td-amt">{fmtCurrency(retainedEarnings.openingBalance.current)}</td>
							{showComparatives && <td className="afs-report__td-amt">{fmtCurrency(retainedEarnings.openingBalance.prior)}</td>}
						</tr>
						<tr>
							<td className="afs-report__td-desc afs-report__td-desc--head">{earningsCaption}</td>
							<td className="afs-report__td-amt">{fmtAmt(retainedEarnings.netIncome.current)}</td>
							{showComparatives && <td className="afs-report__td-amt">{fmtAmt(retainedEarnings.netIncome.prior)}</td>}
						</tr>
						{(retainedEarnings.movements || []).filter(m => m.current || m.prior).map((movement, index) => (
							<tr key={index}>
								<td className="afs-report__td-desc afs-report__td-desc--head">{movement.description}</td>
								<td className="afs-report__td-amt">{fmtAmt(movement.current)}</td>
								{showComparatives && <td className="afs-report__td-amt">{fmtAmt(movement.prior)}</td>}
							</tr>
						))}
						<tr className="afs-report__grand-total-row">
							<td className="afs-report__td-desc afs-report__td-desc--head">{closingCaption}</td>
							<td className="afs-report__td-amt-bold">{fmtCurrency(retainedEarnings.closingBalance.current)}</td>
							{showComparatives && <td className="afs-report__td-amt-bold">{fmtCurrency(retainedEarnings.closingBalance.prior)}</td>}
						</tr>
					</tbody>
				</table>
				<p className="afs-report__page-num">3</p>
			</div>
		);
	};

	// ─── Page 6: Statement of Income ─────────────────────────────────────────
	const IncomeStatementPage = () => {
		if (!incomeStatementRows) return null;

		return (
			<div className="afs-report__page">
				<PageHeader title="Statement of Income" subtitle={`${periodHeading} ${yearEndLabel}`} />
				<table className="afs-report__table afs-report__table--mt-md">
					<thead><YearHeaders /></thead>
					<tbody><StatementRows rows={incomeStatementRows} /></tbody>
				</table>
				<p className="afs-report__page-num">4</p>
			</div>
		);
	};

	// ─── Page 7: Notes ───────────────────────────────────────────────────────
	const NotesPage = () => {
		if (selectedNotes.length === 0 && !ppeNote) return null;

		let noteNum = 0;
		return (
			<div className="afs-report__page">
				<PageHeader title="Notes to Financial Information" subtitle={`${periodHeading} ${yearEndLabel}`} />
				<div className="afs-report__notes">
					{/* Note: Basis of Accounting */}
					{selectedNotes.includes('n_basis') && (
						<div className="afs-report__note">
							<p className="afs-report__h3">{++noteNum}. Basis of Accounting</p>
							<p className="afs-report__text-sm afs-report__text-sm--prewrap">{(noteTexts && noteTexts.n_basis) || NOTE_TEMPLATES.n_basis.template(compilationData)}</p>
						</div>
					)}

					{/* Note: Business description (from compilationData.businessActivity) */}
					{compilationData.businessActivity && (
						<div className="afs-report__note">
							<p className="afs-report__h3">{++noteNum}. Description of Business</p>
							<p className="afs-report__text-sm">{compilationData.businessActivity}</p>
						</div>
					)}

					{/* Note: PP&E continuity */}
					{ppeNote && ppeNote.classes && ppeNote.classes.length > 0 && (
						<div className="afs-report__note">
							<p className="afs-report__h3">{++noteNum}. {ppeNote.heading === "CAPITAL ASSETS" ? "Capital Assets" : "Property, Plant and Equipment"}</p>
							<table className="afs-report__table afs-report__table--mt-sm">
								<thead><YearHeaders /></thead>
								<tbody>
									{ppeNote.classes.map((assetClass, index) => (
										<tr key={index}>
											<td className="afs-report__td-desc afs-report__td-desc--sm">{assetClass.description}</td>
											<td className="afs-report__td-amt afs-report__td-amt--sm">{fmtAmt(assetClass.cost.current)}</td>
											{showComparatives && <td className="afs-report__td-amt afs-report__td-amt--sm">{fmtAmt(assetClass.cost.prior)}</td>}
										</tr>
									))}
									<tr>
										<td className="afs-report__td-desc afs-report__td-desc--sm afs-report__td-desc--bold">Total cost</td>
										<td className="afs-report__td-amt afs-report__td-amt--sm">{fmtAmt(ppeNote.totalCost.current)}</td>
										{showComparatives && <td className="afs-report__td-amt afs-report__td-amt--sm">{fmtAmt(ppeNote.totalCost.prior)}</td>}
									</tr>
									<tr>
										<td className="afs-report__td-desc afs-report__td-desc--sm">Accumulated depreciation</td>
										<td className="afs-report__td-amt afs-report__td-amt--sm">{fmtAmt(ppeNote.accumulatedDepreciation.current)}</td>
										{showComparatives && <td className="afs-report__td-amt afs-report__td-amt--sm">{fmtAmt(ppeNote.accumulatedDepreciation.prior)}</td>}
									</tr>
									<tr className="afs-report__grand-total-row">
										<td className="afs-report__td-desc afs-report__td-desc--bold">Net book value</td>
										<td className="afs-report__td-amt-bold afs-report__td-amt-bold--sm">{fmtCurrency(ppeNote.netBookValue.current)}</td>
										{showComparatives && <td className="afs-report__td-amt-bold afs-report__td-amt-bold--sm">{fmtCurrency(ppeNote.netBookValue.prior)}</td>}
									</tr>
								</tbody>
							</table>
						</div>
					)}

					{/* Other selected notes */}
					{selectedNotes.filter(id => id !== 'n_basis' && id !== 'n_policies').filter(id => !isCustomNote(id) || hasNoteBody(id)).map(id => {
						const noteTitle = isCustomNote(id) ? (customNoteTitleById[id] || "Untitled note") : NOTE_TEMPLATES[id].title;
						const noteBody = isCustomNote(id) ? (noteTexts && noteTexts[id]) || "" : (noteTexts && noteTexts[id]) || NOTE_TEMPLATES[id].template(compilationData);
						return (
							<div key={id} className="afs-report__note">
								<p className="afs-report__h3">{++noteNum}. {noteTitle}</p>
								<p className="afs-report__text-sm afs-report__text-sm--prewrap">{noteBody}</p>
							</div>
						);
					})}
				</div>
				<p className="afs-report__page-num">5</p>
			</div>
		);
	};

	return (
		<div className={watermarkIsShowing ? "afs-report afs-report--draft" : "afs-report"}>
			{statementsAreADraft && (
				<div className="afs-report__draft-toggle">
					<ToolsCheckbox
						label="Show draft watermark"
						checked={this.state.showDraftWatermark}
						onChange={() => this.update({ showDraftWatermark: !this.state.showDraftWatermark })}
					/>
				</div>
			)}
			{openingBalanceIsUnexplainedNil && (
				<div className="afs-report__provenance afs-report__provenance--alert">
					<span className="afs-report__provenance-label">Opening balance</span>
					<span>This client has a prior period, but no opening retained earnings reached the balance sheet. The figure below is missing, not nil — resolve the prior year before signing off.</span>
				</div>
			)}
			{roundingAdjustments.length > 0 && (
				<div className="afs-report__provenance">
					<span className="afs-report__provenance-label">Rounding</span>
					<span>{roundingAdjustments[0].reason}</span>
				</div>
			)}
			{comparativeProvenance && (
				<div className={"afs-report__provenance" + (comparativeProvenance.isSigned ? "" : " afs-report__provenance--unsigned")}>
					<span className="afs-report__provenance-label">Comparatives</span>
					<span>{comparativeProvenance.summary}</span>
				</div>
			)}
			<TitlePage />
			<IndexPage />
			<CompilationPage />
			<BalanceSheetPage />
			<RetainedEarningsPage />
			<IncomeStatementPage />
			<NotesPage />
		</div>
	);
	}
}

export default FullPreviewView;
